import { LogOutIcon } from "lucide-react";
import colorsStore from "shared/store/colorsStore";
import jwtAuthStore from "shared/store/jwtAuthStore";
import { JwtAuthService } from "shared/services/JwtAuthService";
import { auth } from "shared/config/firebase";
import { AuthService } from "shared/services/AuthService";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "shared/components/ui/dropdown-menu";
import { UserImage } from "./UserImage";
import { useEffect, useState, useRef, useCallback, memo } from "react";

// The component implementation
const UserMenuComponent: React.FC = () => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const active = true;

  // Get JWT authentication state
  const { shouldUseJwt, jwtToken } = jwtAuthStore();

  // State for TTL display
  const [ttl, setTtl] = useState<string>("");

  // Refs to store interval IDs
  const ttlCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Ref to store seconds remaining (to avoid dependency issues)
  const secondsRemainingRef = useRef<number | null>(null);

  // Format seconds to minutes:seconds
  const formatTime = useCallback((seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const timeString = `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    return `${t("userMenu.sessionEndsIn")}: ${timeString}`;
  }, []);

  // Handle logout for both authentication methods
  const handleLogout = useCallback(async () => {
    if (shouldUseJwt) {
      // Use JWT logout
      JwtAuthService.logout();
    } else {
      // Use Firebase logout
      AuthService.signOut();
    }
  }, [shouldUseJwt]);

  // Check TTL and set up countdown
  useEffect(() => {
    // Clear any existing intervals when component unmounts or dependencies change
    return () => {
      if (ttlCheckIntervalRef.current) {
        clearInterval(ttlCheckIntervalRef.current);
        ttlCheckIntervalRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, []);

  // Set up TTL checking and countdown when JWT auth is active
  useEffect(() => {
    // Only run when using JWT auth
    if (!(shouldUseJwt && jwtToken)) {
      // Clear intervals if JWT auth is not active
      if (ttlCheckIntervalRef.current) {
        clearInterval(ttlCheckIntervalRef.current);
        ttlCheckIntervalRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    // Function to check TTL
    const checkTtl = async () => {
      try {
        const ttlValue = await JwtAuthService.ttl();

        if (ttlValue <= 0) {
          // Token expired, log out
          handleLogout();
        }
        else {
          // Valid TTL, set seconds remaining
          secondsRemainingRef.current = ttlValue;
          setTtl(formatTime(ttlValue));
        }
      } catch (error) {
        console.error("Error checking token TTL:", error);
        setTtl("----");
      }
    };

    // Set up countdown timer
    const startCountdown = () => {
      // Clear any existing countdown
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      // Set up new countdown
      countdownIntervalRef.current = setInterval(() => {
        if (secondsRemainingRef.current !== null) {
          if (secondsRemainingRef.current > 0) {
            secondsRemainingRef.current -= 1;
            setTtl(formatTime(secondsRemainingRef.current));
          }
          else {
            handleLogout();
          }
        }
      }, 1000);
    };

    // Initial TTL check
    checkTtl();

    // Start countdown
    startCountdown();

    // Set up interval for TTL checks
    ttlCheckIntervalRef.current = setInterval(checkTtl, 15000);

    // Clean up on unmount or when dependencies change
    return () => {
      if (ttlCheckIntervalRef.current) {
        clearInterval(ttlCheckIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [shouldUseJwt, jwtToken, formatTime, handleLogout]);

  // If neither Firebase nor JWT auth is active, don't render the menu
  if (!auth.currentUser && !(shouldUseJwt && jwtToken)) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg duration-150 hover:opacity-80">
          <UserImage />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        alignOffset={5}
        align="end"
        className="bg-white dark:bg-white"
      >
        <DropdownMenuLabel>
          <div>{shouldUseJwt ? (ttl || "----") : auth.currentUser?.email}</div>
          <div className="mt-2 flex items-center gap-2">
            <div
              className={"size-2 shrink-0 rounded-full "}
              style={{
                backgroundColor: active ? colorsConfig.main : "rgb(239 68 68)",
              }}
            />
            <div className="font-normal text-gray-500 dark:text-gray-300">
              {active ? t("userMenu.active") : t("userMenu.inactive")}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOutIcon className="size-4 text-gray-500" />
          {t("userMenu.logOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Export a memoized version of the component
export const UserMenu = memo(UserMenuComponent);
